import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Header, Page, Content } from '@backstage/core-components';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import SecurityIcon from '@material-ui/icons/Security';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

// ─── Types ────────────────────────────────────────────────────────────────────

type RepoSummary = {
  repository: string;
  totalVulns: number;
  openVulns: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  lastScan: string;
};

type Vulnerability = {
  id: number;
  repository: string;
  cve_id: string;
  severity: string;
  package: string;
  installed_version: string;
  fixed_version: string | null;
  first_detected: string;
  last_detected: string;
  is_new: number;
  times_detected: number;
  cve_published_date: string | null;
  is_resolved: number;
  resolved_date: string | null;
  branch_name: string;
  fingerprint: string;
};

type Stats = {
  totalVulns: number;
  openVulns: number;
  resolvedVulns: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  repoCount: number;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  statCard: {
    padding: theme.spacing(2, 3),
    textAlign: 'center',
    borderRadius: 12,
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  severityChip: {
    fontWeight: 700,
    fontSize: '0.7rem',
    minWidth: 72,
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  newBadge: {
    backgroundColor: '#E81823',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.65rem',
    height: 20,
  },
  resolvedBadge: {
    backgroundColor: '#2D9D78',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.65rem',
    height: 20,
  },
  searchField: {
    minWidth: 300,
  },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SEVERITY_ORDER: Record<string, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
  UNKNOWN: 4,
};

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '#D32F2F',
  HIGH: '#E65100',
  MEDIUM: '#F9A825',
  LOW: '#1565C0',
  UNKNOWN: '#757575',
};

function severityChipColor(severity: string): string {
  return SEVERITY_COLORS[severity] || SEVERITY_COLORS.UNKNOWN;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function displayRepoName(name: string): string {
  return name.replace(/^sourcefuse\//i, '');
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

// ─── Component ───────────────────────────────────────────────────────────────

export const TrivyPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRepo = searchParams.get('repo') || null;
  const setSelectedRepo = useCallback(
    (repo: string | null) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (repo) {
          next.set('repo', repo);
        } else {
          next.delete('repo');
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const [repos, setRepos] = useState<RepoSummary[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [vulns, setVulns] = useState<Vulnerability[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [sortField, setSortField] = useState<string>('severity');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const baseUrl = useCallback(
    async () => `${await discoveryApi.getBaseUrl('trivy-tracker')}`,
    [discoveryApi],
  );

  // ── Fetch repos list ──────────────────────────────────────────────────────
  const fetchRepos = useCallback(async () => {
    setLoading(true);
    try {
      const base = await baseUrl();
      const [reposRes, statsRes] = await Promise.all([
        fetchApi.fetch(`${base}/repos`),
        fetchApi.fetch(`${base}/stats`),
      ]);
      setRepos(await reposRes.json());
      setStats(await statsRes.json());
    } catch (e) {
      console.error('trivy-tracker: failed to fetch repos', e);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, fetchApi]);

  // ── Fetch vulns for a repo ────────────────────────────────────────────────
  const fetchVulns = useCallback(
    async (repo: string, resolved: boolean) => {
      setLoading(true);
      try {
        const base = await baseUrl();
        const params = new URLSearchParams();
        if (resolved) params.set('show_resolved', 'true');
        const res = await fetchApi.fetch(
          `${base}/repo/${encodeURIComponent(repo)}?${params}`,
        );
        setVulns(await res.json());
      } catch (e) {
        console.error('trivy-tracker: failed to fetch vulns', e);
      } finally {
        setLoading(false);
      }
    },
    [baseUrl, fetchApi],
  );

  useEffect(() => {
    fetchRepos();
  }, [fetchRepos]);

  useEffect(() => {
    if (selectedRepo) {
      fetchVulns(selectedRepo, showResolved);
    }
  }, [selectedRepo, showResolved, fetchVulns]);

  // ── Sorting ───────────────────────────────────────────────────────────────
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedVulns = [...vulns].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'severity') {
      return ((SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99)) * dir;
    }
    if (sortField === 'cve_id') return a.cve_id.localeCompare(b.cve_id) * dir;
    if (sortField === 'package') return (a.package || '').localeCompare(b.package || '') * dir;
    if (sortField === 'last_detected') {
      return (new Date(a.last_detected).getTime() - new Date(b.last_detected).getTime()) * dir;
    }
    if (sortField === 'times_detected') return ((a.times_detected || 0) - (b.times_detected || 0)) * dir;
    return 0;
  });

  const filteredVulns = sortedVulns.filter(v => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      v.cve_id.toLowerCase().includes(s) ||
      v.package.toLowerCase().includes(s) ||
      v.severity.toLowerCase().includes(s) ||
      (v.fixed_version || '').toLowerCase().includes(s) ||
      (v.installed_version || '').toLowerCase().includes(s)
    );
  });

  const filteredRepos = repos.filter(r => {
    if (!search) return true;
    return r.repository.toLowerCase().includes(search.toLowerCase());
  });

  // ── Repo detail view ──────────────────────────────────────────────────────
  if (selectedRepo) {
    // Severity summary for selected repo
    const severityCounts: Record<string, number> = {};
    vulns.forEach(v => {
      if (v.is_resolved === 0) {
        severityCounts[v.severity] = (severityCounts[v.severity] || 0) + 1;
      }
    });

    return (
      <Page themeId="tool">
        <Header title="Trivy Vulnerability Scanner" subtitle="Security scanning powered by Trivy" />
        <Content>
          <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => { setSelectedRepo(null); setVulns([]); setSearch(''); setShowResolved(false); }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" style={{ fontWeight: 700 }}>
                {displayRepoName(selectedRepo)}
              </Typography>
              <Box ml={2} display="flex" style={{ gap: 8 }}>
                {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(sev => (
                  <Chip
                    key={sev}
                    label={`${sev}: ${severityCounts[sev] || 0}`}
                    size="small"
                    className={classes.severityChip}
                    style={{ backgroundColor: severityChipColor(sev), color: '#fff' }}
                  />
                ))}
              </Box>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showResolved}
                    onChange={e => setShowResolved(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Show resolved"
              />
              <TextField
                className={classes.searchField}
                size="small"
                variant="outlined"
                placeholder="Search CVE, package, severity..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              <Tooltip title="Refresh">
                <IconButton onClick={() => fetchVulns(selectedRepo, showResolved)} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : filteredVulns.length === 0 ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Typography color="textSecondary">
                {search ? 'No vulnerabilities match your search' : 'No open vulnerabilities found'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel active={sortField === 'severity'} direction={sortField === 'severity' ? sortDir : 'asc'} onClick={() => handleSort('severity')}>
                        Severity
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={sortField === 'cve_id'} direction={sortField === 'cve_id' ? sortDir : 'asc'} onClick={() => handleSort('cve_id')}>
                        CVE ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={sortField === 'package'} direction={sortField === 'package' ? sortDir : 'asc'} onClick={() => handleSort('package')}>
                        Package
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Installed</TableCell>
                    <TableCell>Fixed In</TableCell>
                    <TableCell>
                      <TableSortLabel active={sortField === 'last_detected'} direction={sortField === 'last_detected' ? sortDir : 'asc'} onClick={() => handleSort('last_detected')}>
                        Last Detected
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>First Detected</TableCell>
                    <TableCell>
                      <TableSortLabel active={sortField === 'times_detected'} direction={sortField === 'times_detected' ? sortDir : 'asc'} onClick={() => handleSort('times_detected')}>
                        Scans
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVulns.map(v => (
                    <TableRow key={v.id}>
                      <TableCell>
                        <Chip
                          label={v.severity}
                          size="small"
                          className={classes.severityChip}
                          style={{ backgroundColor: severityChipColor(v.severity), color: '#fff' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                          <a
                            href={`https://nvd.nist.gov/vuln/detail/${v.cve_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#0469E3', textDecoration: 'none', fontWeight: 600 }}
                          >
                            {v.cve_id}
                          </a>
                          {v.is_new === 1 && (
                            <Chip label="NEW" size="small" className={classes.newBadge} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{v.package}</TableCell>
                      <TableCell style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{v.installed_version}</TableCell>
                      <TableCell style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: v.fixed_version ? '#2D9D78' : '#999' }}>
                        {v.fixed_version || '—'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatDate(v.last_detected)}>
                          <span>{timeAgo(v.last_detected)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={formatDate(v.first_detected)}>
                          <span>{timeAgo(v.first_detected)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{v.times_detected}</TableCell>
                      <TableCell style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{v.branch_name}</TableCell>
                      <TableCell>
                        {v.is_resolved === 1 ? (
                          <Chip label="RESOLVED" size="small" className={classes.resolvedBadge} />
                        ) : (
                          <Chip label="OPEN" size="small" style={{ backgroundColor: '#FFF3E0', color: '#E65100', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Content>
      </Page>
    );
  }

  // ── Repos list view ───────────────────────────────────────────────────────
  return (
    <Page themeId="tool">
      <Header title="Trivy Vulnerability Scanner" subtitle="Security scanning powered by Trivy" />
      <Content>
        {/* Stats cards */}
        {stats && (
          <Box mb={3}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3} md>
                <Paper className={classes.statCard}>
                  <Typography className={classes.statValue}>{stats.repoCount}</Typography>
                  <Typography className={classes.statLabel}>Repos Scanned</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md>
                <Paper className={classes.statCard}>
                  <Typography className={classes.statValue}>{stats.openVulns}</Typography>
                  <Typography className={classes.statLabel}>Open Vulns</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md>
                <Paper className={classes.statCard} style={{ borderLeft: `4px solid ${SEVERITY_COLORS.CRITICAL}` }}>
                  <Typography className={classes.statValue} style={{ color: SEVERITY_COLORS.CRITICAL }}>{stats.criticalCount}</Typography>
                  <Typography className={classes.statLabel}>Critical</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md>
                <Paper className={classes.statCard} style={{ borderLeft: `4px solid ${SEVERITY_COLORS.HIGH}` }}>
                  <Typography className={classes.statValue} style={{ color: SEVERITY_COLORS.HIGH }}>{stats.highCount}</Typography>
                  <Typography className={classes.statLabel}>High</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md>
                <Paper className={classes.statCard} style={{ borderLeft: `4px solid ${SEVERITY_COLORS.MEDIUM}` }}>
                  <Typography className={classes.statValue} style={{ color: SEVERITY_COLORS.MEDIUM }}>{stats.mediumCount}</Typography>
                  <Typography className={classes.statLabel}>Medium</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md>
                <Paper className={classes.statCard} style={{ borderLeft: `4px solid ${SEVERITY_COLORS.LOW}` }}>
                  <Typography className={classes.statValue} style={{ color: SEVERITY_COLORS.LOW }}>{stats.lowCount}</Typography>
                  <Typography className={classes.statLabel}>Low</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3} md>
                <Paper className={classes.statCard}>
                  <Typography className={classes.statValue} style={{ color: '#2D9D78' }}>{stats.resolvedVulns}</Typography>
                  <Typography className={classes.statLabel}>Resolved</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Search + Refresh */}
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" style={{ fontWeight: 700 }}>Repositories</Typography>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <TextField
              className={classes.searchField}
              size="small"
              variant="outlined"
              placeholder="Search repositories..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchRepos} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : filteredRepos.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={6}>
            <SecurityIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
            <Typography color="textSecondary">
              {search ? 'No repositories match your search' : 'No Trivy scan data yet'}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Send scan results via POST /api/trivy-tracker/record
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Repository</TableCell>
                  <TableCell style={{ textAlign: 'center' }}>Open Vulns</TableCell>
                  <TableCell style={{ textAlign: 'center', color: SEVERITY_COLORS.CRITICAL }}>Critical</TableCell>
                  <TableCell style={{ textAlign: 'center', color: SEVERITY_COLORS.HIGH }}>High</TableCell>
                  <TableCell style={{ textAlign: 'center', color: SEVERITY_COLORS.MEDIUM }}>Medium</TableCell>
                  <TableCell style={{ textAlign: 'center', color: SEVERITY_COLORS.LOW }}>Low</TableCell>
                  <TableCell>Last Scan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRepos.map(r => (
                  <TableRow
                    key={r.repository}
                    className={classes.tableRow}
                    onClick={() => setSelectedRepo(r.repository)}
                  >
                    <TableCell>
                      <Typography style={{ fontWeight: 600 }}>{displayRepoName(r.repository)}</Typography>
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                      <Chip
                        label={r.openVulns}
                        size="small"
                        style={{
                          fontWeight: 700,
                          backgroundColor: r.openVulns === 0 ? '#E8F5E9' : '#FFF3E0',
                          color: r.openVulns === 0 ? '#2D9D78' : '#E65100',
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 700, color: r.criticalCount > 0 ? SEVERITY_COLORS.CRITICAL : '#ccc' }}>
                      {r.criticalCount}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 700, color: r.highCount > 0 ? SEVERITY_COLORS.HIGH : '#ccc' }}>
                      {r.highCount}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 700, color: r.mediumCount > 0 ? SEVERITY_COLORS.MEDIUM : '#ccc' }}>
                      {r.mediumCount}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', fontWeight: 700, color: r.lowCount > 0 ? SEVERITY_COLORS.LOW : '#ccc' }}>
                      {r.lowCount}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDate(r.lastScan)}>
                        <span>{timeAgo(r.lastScan)}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Content>
    </Page>
  );
};
